import { CrewRadioGroup } from 'components/devextreme/crewRadioGroup'
import { PaymentMethod } from 'enums/app'
import { usePaymentMethodDataSource } from 'hooks/dataSource/useResourceDataSource'
import { FC, memo, useCallback, useMemo, useState } from 'react'
import { ContractRegisterCreditCardForm } from './components/contractRegisterCreditCardForm/contractRegisterCreditCardForm'
import { ContractRegisterBankTransferForm } from './components/contractRegisterBankTransferForm/contractRegisterBankTransferForm'
import { Elements } from '@stripe/react-stripe-js'
import {
  useCreateSetupIntentQuery,
  useGetAiCreditPriceTiersForPlansQuery,
  useGetBillingCycleQuery,
  useGetCurrentSubscriptionItemsCountQuery,
  useGetPlansQuery,
  useGetStorageCapacityPriceTiersForPlansQuery,
} from '@crew/apis/contract/contractApis'
import { StripeElementsOptions, loadStripe } from '@stripe/stripe-js'
import { NativeEventInfo } from 'devextreme/events'
import dxRadioGroup from 'devextreme/ui/radio_group'
import { ValueChangedInfo } from 'devextreme/ui/editor/editor'
import { useAppSelector } from 'states/hooks'
import { CrewFieldLabel } from 'components/elements/crewFieldLabel'
import { useTranslation } from '@crew/modules/i18n'
import {
  BillingCycle,
  StripeSubscriptionStatus,
  ContractPlan,
} from '@crew/enums/app'
import { ComponentCallbackHandler } from '@crew/utils'
import { useValueChangeEffect } from '@crew/hooks'
import { CrewNumberBoxField } from 'components/forms/crewNumberBoxField'
import { MINIMUM_STORAGE_CAPACITY } from '@crew/configs/constants'
import { ValueChangedEvent as NumberBoxValueChangedEvent } from 'devextreme/ui/number_box'
import { useForm, useWatch } from 'react-hook-form'
import { GetCurrentSubscriptionItemsCountRequest } from '@crew/apis/contract/models/getCurrentSubscriptionItemsCount/request'
import { skipToken } from '@reduxjs/toolkit/query'
import { GetAiCreditPriceTiersForPlansResponse_PriceTier } from '@crew/apis/contract/models/getAiCreditPriceTiersForPlans/response'
import { GetStorageCapacityPriceTiersForPlansResponse_PriceTier } from '@crew/apis/contract/models/getStorageCapacityPriceTiersForPlans/response'
import { useSearchParams } from 'react-router-dom'

// プラン情報のラジオボタンの選択値の型
type PlanRadioGroupItem = {
  value: ContractPlan
  name: string
  description: string
}

// プラン情報のラジオボタンの選択値の型
type BillingCycleRadioGroupItem = {
  value: BillingCycle
  name: string
  description: string
}

// render content
// renderとして使うのでmemo不可
const PlanItem: FC<PlanRadioGroupItem> = (props) => (
  <div
    id={`plan-radioId-${props.value}`}
    className="w-full h-10 justify-start items-center inline-flex"
  >
    {/* プラン名 */}
    <div className="w-40 justify-start items-center gap-2.5 flex">
      <div className="text-md">{props.name}</div>
    </div>
    {/* 説明 */}
    <div className="gap-2.5 flex">
      <div className="crew-text-gray-4 text-md">{props.description}</div>
    </div>
  </div>
)

// render content
// renderとして使うのでmemo不可
const BillingCycleItem: FC<BillingCycleRadioGroupItem> = (props) => (
  <div
    id={`cycle-radioId-${props.value}`}
    className="w-full h-10 justify-start items-center inline-flex"
  >
    {/* 契約サイクル */}
    <span className="leading-5 w-20">{props.name}</span>
    {/* 説明 */}
    <div className="w-48 crew-text-gray-5 leading-tight">
      {props.description}
    </div>
  </div>
)

type FormValues = {
  contractUserCount: number
  contractExternalUserAdditionalCount: number
  contractS3StorageCapacity: number
  contractAdditionalAiCreditCount: number
}
const formInitialValues: FormValues = {
  contractUserCount: 0,
  contractExternalUserAdditionalCount: 0,
  contractS3StorageCapacity: 0,
  contractAdditionalAiCreditCount: 0,
}
export const ContractRegisterPaymentMethodPage: FC = memo(() => {
  const { control, setValue } = useForm<FormValues>({
    criteriaMode: 'all',
  })

  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const paymentMethodDataSource = usePaymentMethodDataSource()
  const themeMode = useAppSelector((state) => state.app.currentTheme)

  // 請求サイクルを取得する
  const { data: billingCycleData } = useGetBillingCycleQuery()

  // プラン情報一覧を取得
  const { data: plansResult, isFetching } = useGetPlansQuery()

  // 契約プランのラジオボタンに値を渡すための変数
  const [selectedPlan, setSelectedPlan] = useState<ContractPlan | undefined>(
    undefined
  )

  // 請求サイクルのラジオボタンに値を渡すための変数
  const [selectedBillingCycle, setSelectedBillingCycle] =
    useState<BillingCycle>(BillingCycle.Month)

  // 支払い方法
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>(
    PaymentMethod.creditCard.value
  )

  const {
    data: createSetupIntentResult,
    isFetching: isCreateSetupIntentFetching,
  } = useCreateSetupIntentQuery()

  // プラン一覧
  const planItems = useMemo(() => {
    const items: PlanRadioGroupItem[] = []
    plansResult?.plans.map((item) =>
      items.push({
        value: item.plan,
        name: t(`label.planType.${item.plan}`),
        description: t(`label.planDescriptionForUpdate.${item.plan}`),
      })
    )

    return items
  }, [plansResult?.plans, t])

  // 契約サイクル一覧
  const billingItems = useMemo(() => {
    // 選択中のプランの契約サイクルの情報を取得
    const plan = plansResult?.plans.find((item) => item.plan === selectedPlan)
    if (!plan) {
      return []
    }

    return Object.values(BillingCycle).map((cycle) => {
      // プラン情報から各請求サイクルの情報を取得
      const billingCycle = plan.billingCycles.find(
        (item) => item.billingCycle === cycle
      )
      if (!billingCycle) {
        // undefinedになることはないが、型制約のためチェック処理を追加
        return []
      }

      const item: BillingCycleRadioGroupItem = {
        value: cycle,
        name: t(`label.cycleBilling.${cycle}.name`),
        description: t(`label.cycleBilling.${cycle}.description`),
      }

      return item
    })
  }, [plansResult?.plans, selectedPlan, t])

  // URLパラメータにplanが指定されている場合は、選択中のプランに反映する
  useValueChangeEffect(
    () => {
      const planParam = searchParams.get('plan')

      if (
        planParam &&
        Object.values(ContractPlan).includes(planParam as ContractPlan)
      ) {
        setSelectedPlan(planParam as ContractPlan)
      }
    },
    [searchParams],
    searchParams
  )

  // プラン一覧取得時、現在契約中のプランをラジオボタンの選択値に反映する
  useValueChangeEffect(
    () => {
      // URLパラメータにplanが指定されている場合はそちらを優先するため、処理を中断する
      const planParam = searchParams.get('plan')
      if (
        planParam &&
        Object.values(ContractPlan).includes(planParam as ContractPlan)
      ) {
        return
      }

      // プラン一覧から現在契約中のプラン情報を検索
      const currentPlan = plansResult?.plans.find((item) =>
        item.billingCycles.some((billingCycle) => billingCycle.isCurrentCycle)
      )
      // 現在契約中のプラン情報があれば選択状態にする
      // ただし、契約中のプランがフリープランの場合は、アップグレードの処理となるためスタンダードを選択状態にする
      if (currentPlan && currentPlan.plan !== ContractPlan.Free) {
        setSelectedPlan(currentPlan.plan)
      } else {
        setSelectedPlan(ContractPlan.Standard)
      }
    },
    [plansResult, searchParams],
    plansResult
  )

  // 請求サイクルを取得できたらラジオボタンの選択値に反映する
  useValueChangeEffect(
    () => {
      if (billingCycleData) {
        billingCycleData.billingCycle.forEach((item) => {
          if (item.isCurrentCycle) {
            setSelectedBillingCycle(item.billingCycle)
          }
        })
      }
    },
    [billingCycleData],
    billingCycleData
  )

  // 契約プランのラジオボタンの選択値が変わったときのイベントハンドラ
  const handlePlanRadioGroupValueChanged = useCallback<
    ComponentCallbackHandler<typeof CrewRadioGroup, 'onValueChanged'>
  >((event) => {
    setSelectedPlan(event.value)
  }, [])

  // 契約サイクルのラジオボタンの選択値が変わったときのイベントハンドラ
  const handleBillingCycleRadioGroupValueChanged = useCallback<
    ComponentCallbackHandler<typeof CrewRadioGroup, 'onValueChanged'>
  >((event) => {
    setSelectedBillingCycle(event.value)
  }, [])

  // 支払方法変更時のイベントハンドラ
  const handlePaymentMethodChanged = useCallback(
    (e: NativeEventInfo<dxRadioGroup, Event> & ValueChangedInfo) => {
      setSelectedPaymentMethod(e.value)
    },
    []
  )

  // 一般ユーザー購入数のナンバーボックスの値変更時の処理、小数点の入力をさせない
  const handleNumberOfContractUserCountValueChanged = useCallback(
    (e: NumberBoxValueChangedEvent) => {
      if (e.value !== undefined) {
        const newValue = Math.floor(e.value)
        setValue('contractUserCount', newValue)
      }
    },
    [setValue]
  )

  // 外部ユーザー購入数のナンバーボックスの値変更時の処理、小数点の入力をさせない
  const handleNumberOfContractExternalUserAdditionalCountValueChanged =
    useCallback(
      (e: NumberBoxValueChangedEvent) => {
        if (e.value !== undefined) {
          const newValue = Math.floor(e.value)
          setValue('contractExternalUserAdditionalCount', newValue)
        }
      },
      [setValue]
    )

  // ストレージ容量のナンバーボックスの値変更時の処理、10単位での入力にする
  const handleNumberOfContractStorageCapacityValueChanged = useCallback(
    (e: NumberBoxValueChangedEvent) => {
      if (e.value !== undefined) {
        // 10単位での入力にする
        const newValue = Math.floor(e.value / 10) * 10
        setValue('contractS3StorageCapacity', newValue)
      }
    },
    [setValue]
  )

  // AIクレジット数のナンバーボックスの値変更時の処理、10単位での入力にする
  const handleNumberOfContractAdditionalAiCreditCountValueChanged = useCallback(
    (e: NumberBoxValueChangedEvent) => {
      if (e.value !== undefined) {
        // 10単位での入力にする
        const newValue = Math.floor(e.value / 10) * 10
        setValue('contractAdditionalAiCreditCount', newValue)
      }
    },
    [setValue]
  )

  // Stripe要素のオプション
  const stripeElementOptions = useMemo<StripeElementsOptions>(() => {
    return {
      appearance: {
        theme: themeMode === 'light' ? 'flat' : 'night',
      },
      clientSecret: createSetupIntentResult?.clientSecret,
    }
  }, [createSetupIntentResult?.clientSecret, themeMode])

  // トライアル中の場合は契約サイクルを表示するため、サブスクリプションステータスを取得する
  const isTrialing =
    createSetupIntentResult?.subscriptionStatus ===
    StripeSubscriptionStatus.Trialing

  const isFree = createSetupIntentResult?.currentPlan === ContractPlan.Free

  const isPaid = !isTrialing && !isFree

  // 現在のサブスクリプションアイテムの契約数を取得する（初期値設定のため）
  // isPaidである場合は、画面で購入数を入力しないため、現在の契約数は取得する必要がない
  const getCurrentSubscriptionItemsCountParams:
    | GetCurrentSubscriptionItemsCountRequest
    | undefined =
    !isPaid && selectedPlan !== undefined
      ? {
          targetPlan: selectedPlan,
        }
      : undefined
  const { data: getCurrentSubscriptionItemsCountResult } =
    useGetCurrentSubscriptionItemsCountQuery(
      getCurrentSubscriptionItemsCountParams ?? skipToken
    )

  // ストレージの単価を取得
  const {
    data: storageCapacityPriceTiersForPlansData,
    isFetching: isStorageCapacityPriceTiersForPlansFetching,
  } = useGetStorageCapacityPriceTiersForPlansQuery()

  // AIクレジットの単価を取得
  const {
    data: aiCreditPriceTiersForPlansData,
    isFetching: isAiCreditPriceTiersForPlansFetching,
  } = useGetAiCreditPriceTiersForPlansQuery()

  // 入力されている一般ユーザー数の値
  const inputtedContractUserCount = useWatch({
    control,
    name: 'contractUserCount',
    defaultValue: formInitialValues.contractUserCount,
  })
  // 入力されている外部ユーザー数の値
  const inputtedContractExternalUserAdditionalCount = useWatch({
    control,
    name: 'contractExternalUserAdditionalCount',
    defaultValue: formInitialValues.contractExternalUserAdditionalCount,
  })
  // 入力されているストレージオプションの値
  const inputtedContractS3StorageCapacity = useWatch({
    control,
    name: 'contractS3StorageCapacity',
    defaultValue: formInitialValues.contractS3StorageCapacity,
  })
  // 入力されているAIクレジットの値
  const inputtedContractAdditionalAiCreditCount = useWatch({
    control,
    name: 'contractAdditionalAiCreditCount',
    defaultValue: formInitialValues.contractAdditionalAiCreditCount,
  })

  // 現在選択しているプランのAIクレジットの無料枠数
  const freeAiCreditCountForSelectedPlan = useMemo(() => {
    if (!getCurrentSubscriptionItemsCountResult) {
      return 0
    }

    if (selectedPlan === ContractPlan.Standard) {
      return getCurrentSubscriptionItemsCountResult.standardFreeAiCredit
    } else if (selectedPlan === ContractPlan.Professional) {
      return getCurrentSubscriptionItemsCountResult.professionalFreeAiCredit
    }

    return 0
  }, [getCurrentSubscriptionItemsCountResult, selectedPlan])

  useValueChangeEffect(
    () => {
      // 現在のサブスクリプションアイテムの契約数が取得できた場合は、フォームの初期値を設定する
      // なお、初期値だけでなく「選択したプランに応じた最小契約数を下回っている場合」も、その最小契約数が設定されるようにしている
      if (getCurrentSubscriptionItemsCountResult) {
        // 一般ユーザー数
        // 入力値が必要最小契約数を下回っている場合は最小契約数の値を設定する
        const requiredMinimumContractUserCount =
          getCurrentSubscriptionItemsCountResult.requiredMinimumContractUserCount

        const queryResultContractUserCount =
          getCurrentSubscriptionItemsCountResult.contractUserCount

        if (isFree) {
          // フリープランからのアップグレードの場合は、入力値が必要最小契約数を下回っている場合は最小契約数の値を設定する
          if (inputtedContractUserCount <= requiredMinimumContractUserCount) {
            setValue('contractUserCount', requiredMinimumContractUserCount)
          }
        } else {
          if (inputtedContractUserCount <= requiredMinimumContractUserCount) {
            // 入力値が最小契約数より小さい場合は、クエリで取得したユーザー契約数と、最小契約数を比較し、大きい方をセットする
            setValue(
              'contractUserCount',
              Math.max(
                requiredMinimumContractUserCount,
                queryResultContractUserCount
              )
            )
          }
        }

        // 外部ユーザー数
        // 入力値が必要最小契約数を下回っている場合は最小契約数の値を設定する
        const requiredMinimumContractExternalUserAdditionalCount =
          getCurrentSubscriptionItemsCountResult.requiredMinimumContractExternalUserAdditionalCount

        const queryResultContractExternalUserAdditionalCount =
          getCurrentSubscriptionItemsCountResult.contractExternalUserAdditionalCount

        if (isFree) {
          // フリープランからのアップグレードの場合は、入力値が必要最小契約数を下回っている場合は最小契約数の値を設定する
          if (
            inputtedContractExternalUserAdditionalCount <=
            requiredMinimumContractExternalUserAdditionalCount
          ) {
            setValue(
              'contractExternalUserAdditionalCount',
              requiredMinimumContractExternalUserAdditionalCount
            )
          }
        } else {
          if (
            inputtedContractExternalUserAdditionalCount <=
            requiredMinimumContractExternalUserAdditionalCount
          ) {
            // 入力値が最小契約数より小さい場合は、初期化のためにクエリで取得した外部ユーザー契約数と、最小契約数を比較し、大きい方をセットする
            setValue(
              'contractExternalUserAdditionalCount',
              Math.max(
                requiredMinimumContractExternalUserAdditionalCount,
                queryResultContractExternalUserAdditionalCount
              )
            )
          }
        }

        // ストレージ容量
        // ※いずれもGB単位で扱っている値のため、バイト変換は不要
        // 入力値が必要最小契約容量を下回っている場合は最小契約容量の値を設定する
        const requiredMinimumContractS3StorageCapacity =
          getCurrentSubscriptionItemsCountResult.requiredMinimumContractS3StorageCapacity

        const queryResultContractS3StorageCapacity =
          getCurrentSubscriptionItemsCountResult.contractS3StorageCapacity

        if (isFree) {
          // フリープランからのアップグレードの場合は、入力値が必要最小契約数を下回っている場合は最小契約数の値を設定する
          if (
            inputtedContractS3StorageCapacity <=
            requiredMinimumContractS3StorageCapacity
          ) {
            setValue(
              'contractS3StorageCapacity',
              requiredMinimumContractS3StorageCapacity
            )
          }
        } else {
          if (
            inputtedContractS3StorageCapacity <=
            requiredMinimumContractS3StorageCapacity
          ) {
            // 入力値が最小契約数より小さい場合は、初期化のためにクエリで取得したs3契約容量と、最小契約数を比較し、大きい方をセットする
            setValue(
              'contractS3StorageCapacity',
              Math.max(
                requiredMinimumContractS3StorageCapacity,
                queryResultContractS3StorageCapacity
              )
            )
          }
        }

        // AIクレジット数（追加分）
        const queryResultContractAdditionalAiCreditCount =
          getCurrentSubscriptionItemsCountResult.contractAdditionalAiCreditCount

        // 入力値が未設定の場合のみ、取得した値を設定する
        if (!inputtedContractAdditionalAiCreditCount) {
          setValue(
            'contractAdditionalAiCreditCount',
            queryResultContractAdditionalAiCreditCount
          )
        }
      }
    },
    [
      getCurrentSubscriptionItemsCountResult,
      inputtedContractExternalUserAdditionalCount,
      inputtedContractAdditionalAiCreditCount,
      inputtedContractS3StorageCapacity,
      inputtedContractUserCount,
      isFree,
      setValue,
    ],
    getCurrentSubscriptionItemsCountResult
  )

  // 請求方法のラジオボタンのrender
  const renderPaymentRadioGroupItem = useCallback(
    (item: { id: string; name: string }) => {
      return (
        <div
          id={`plan-radioId-${item.id}`}
          className="w-full h-10 justify-start items-center inline-flex"
        >
          {/* 支払い方法 */}
          <div className="w-28 justify-start items-center gap-2.5 flex">
            <div className="text-md">{t(item.name)}</div>
          </div>
        </div>
      )
    },
    [t]
  )

  /**
   * 基本サービス利用料に関わる計算・表示処理
   */

  // 基本サービス利用料の単価
  const serviceUnitAmount = useMemo(() => {
    // 選択中のプランの契約サイクルの情報を取得
    const plan = plansResult?.plans.find((item) => item.plan === selectedPlan)
    if (!plan) {
      // undefinedになることはないが、undefinedになっていると正しい単価が取得できないのでundefinedを返すようにしておく
      return
    }
    // プラン情報から各請求サイクルの情報を取得
    const billingCycle = plan.billingCycles.find(
      (item) => item.billingCycle === selectedBillingCycle
    )
    if (!billingCycle) {
      // undefinedになることはないが、undefinedになっていると正しい単価が取得できないのでundefinedを返すようにしておく
      return
    }

    return billingCycle.serviceUnitAmount
  }, [plansResult?.plans, selectedBillingCycle, selectedPlan])

  // 基本サービス利用料の合計金額
  const serviceFeeAmount = useMemo(() => {
    // 単価がundefinedの場合は、正しい表示にならない恐れがあるのでundefinedを返すようにしておく
    if (serviceUnitAmount === undefined) {
      return
    }

    // 基本サービス利用料 = 基本サービス利用料(1ユーザーあたり) * 入力されている値
    return serviceUnitAmount * inputtedContractUserCount
  }, [inputtedContractUserCount, serviceUnitAmount])

  // 基本サービス利用料の計算式表示
  const renderServiceFeeFormula = useCallback(() => {
    // 単価がundefinedの場合は、正しい表示にならない恐れがあるので何も表示しない
    if (serviceUnitAmount === undefined) {
      return ''
    }

    return t('label.cycleBilling.priceWithQuantity', {
      price: serviceUnitAmount.toLocaleString(),
    })
  }, [serviceUnitAmount, t])

  // 基本サービス利用料の金額表示
  const renderServiceFeeAmount = useCallback(() => {
    // 金額がundefinedの場合は、正しい表示にならない恐れがあるので何も表示しない
    if (serviceFeeAmount === undefined) {
      return ''
    }

    return t('label.cycleBilling.priceAmount', {
      price: serviceFeeAmount.toLocaleString(),
    })
  }, [t, serviceFeeAmount])

  /**
   * オプション利用料(外部ユーザー)に関わる計算・表示処理
   */

  // オプション利用料(外部ユーザー)の無料枠数
  const freeExtUserCountForSelectedPlan = useMemo(() => {
    if (!inputtedContractUserCount) {
      return 0
    }

    return inputtedContractUserCount * 2
  }, [inputtedContractUserCount])

  // オプション利用料(外部ユーザー)の単価
  const extUserUnitAmount = useMemo(() => {
    // 選択中のプランの契約サイクルの情報を取得
    const plan = plansResult?.plans.find((item) => item.plan === selectedPlan)
    if (!plan) {
      // undefinedになることはないが、undefinedになっていると正しい単価が取得できないのでundefinedを返すようにしておく
      return
    }
    // プラン情報から各請求サイクルの情報を取得
    const billingCycle = plan.billingCycles.find(
      (item) => item.billingCycle === selectedBillingCycle
    )
    if (!billingCycle) {
      // undefinedになることはないが、undefinedになっていると正しい単価が取得できないのでundefinedを返すようにしておく
      return
    }

    return billingCycle.extUserUnitAmount
  }, [plansResult?.plans, selectedBillingCycle, selectedPlan])

  // オプション利用料(外部ユーザー)の合計金額
  const extUserFeeAmount = useMemo(() => {
    // 単価がundefinedの場合は、正しい表示にならない恐れがあるのでundefinedを返すようにしておく
    if (extUserUnitAmount === undefined) {
      return
    }

    // オプション利用料(外部ユーザー) = オプション利用料(1ユーザーあたり) * 入力されている値
    return extUserUnitAmount * inputtedContractExternalUserAdditionalCount
  }, [extUserUnitAmount, inputtedContractExternalUserAdditionalCount])

  // オプション利用料(外部ユーザー)の計算式表示
  const renderExtUserFeeFormula = useCallback(() => {
    // 単価がundefinedの場合は、正しい表示にならない恐れがあるので何も表示しない
    if (extUserUnitAmount === undefined) {
      return ''
    }

    return t('label.cycleBilling.priceWithQuantity', {
      price: extUserUnitAmount.toLocaleString(),
    })
  }, [extUserUnitAmount, t])

  // オプション利用料(外部ユーザー)の金額表示
  const renderExtUserFeeAmount = useCallback(() => {
    // 金額がundefinedの場合は、正しい表示にならない恐れがあるので何も表示しない
    if (extUserFeeAmount === undefined) {
      return ''
    }

    return t('label.cycleBilling.priceAmount', {
      price: extUserFeeAmount.toLocaleString(),
    })
  }, [t, extUserFeeAmount])

  /**
   * オプション利用料(ストレージ)に関わる計算・表示処理
   */

  // ストレージ容量の金額を計算する関数
  const calculateStorageCapacityAmount = useCallback(
    (
      capacity: number,
      tiers: GetStorageCapacityPriceTiersForPlansResponse_PriceTier[]
    ) => {
      let amount = 0

      for (const tier of tiers) {
        const unitAmount = tier.unitAmountDecimal

        if (tier.upTo !== null && tier.upTo > 0) {
          // 段階的ティアが続く場合
          let calculateCapacity: number
          if (capacity <= tier.upTo) {
            calculateCapacity = capacity
          } else {
            calculateCapacity = tier.upTo
          }
          amount += calculateCapacity * unitAmount
          capacity -= calculateCapacity
        } else {
          // 最後のティア（up_to === null または 0）
          amount += capacity * unitAmount
          capacity = 0
        }

        if (capacity === 0) {
          break
        }
      }

      return amount
    },
    []
  )

  // オプション利用料(ストレージ)の合計金額
  const storageFeeAmount = useMemo(() => {
    // 単価がundefinedの場合は、正しい表示にならない恐れがあるのでundefinedを返すようにしておく
    if (
      storageCapacityPriceTiersForPlansData === undefined ||
      isStorageCapacityPriceTiersForPlansFetching
    ) {
      return
    }

    // 選択されているプランや請求サイクルに応じた単価を取得
    const priceTiers = storageCapacityPriceTiersForPlansData.plans
      .find((item) => item.plan === selectedPlan)
      ?.billingCycles.find(
        (item) => item.billingCycle === selectedBillingCycle
      )?.priceTiers

    if (priceTiers === undefined) {
      // undefinedになることはないが、undefinedになっていると正しい単価が取得できないのでundefinedを返すようにしておく
      return
    }

    // ストレージ容量の金額を計算
    const calcAmount = calculateStorageCapacityAmount(
      inputtedContractS3StorageCapacity,
      priceTiers
    )
    if (calcAmount === undefined) {
      // 該当するティアが見つからなかった場合は、正しい表示にならない恐れがあるのでundefinedを返すようにしておく
      return
    }

    return calcAmount
  }, [
    storageCapacityPriceTiersForPlansData,
    isStorageCapacityPriceTiersForPlansFetching,
    calculateStorageCapacityAmount,
    inputtedContractS3StorageCapacity,
    selectedPlan,
    selectedBillingCycle,
  ])

  // オプション利用料(ストレージ)の金額表示
  const renderStorageFeeAmount = useCallback(() => {
    // 金額がundefinedの場合は、正しい表示にならない恐れがあるので何も表示しない
    if (storageFeeAmount === undefined) {
      return ''
    }

    return t('label.cycleBilling.priceAmount', {
      price: storageFeeAmount.toLocaleString(),
    })
  }, [t, storageFeeAmount])

  /**
   * オプション利用料(AIクレジット)に関わる計算・表示処理
   */

  // AIクレジットの金額を計算する関数
  const calculateAiCreditAmount = useCallback(
    (
      creditCount: number,
      tiers: GetAiCreditPriceTiersForPlansResponse_PriceTier[]
    ) => {
      // 引数のクレジット数に該当する単価を格納する変数
      let targetTier:
        | GetAiCreditPriceTiersForPlansResponse_PriceTier
        | undefined = undefined

      // ティアごとに単価が異なるので、ティア内から該当する単価を探す
      for (const tier of tiers) {
        if (tier.upTo === 0) {
          // upToが0の場合は、最上位のティアなのでその単価を使用する
          targetTier = tier
          break
        } else if (creditCount > tier.upTo) {
          // upToよりも引数のクレジット数が多い場合は、さらに上のティアを探すためcontinueする
          continue
        } else if (creditCount <= tier.upTo) {
          // 該当するティアを見つけたらその単価を使用
          targetTier = tier
          break
        }
      }

      // 通常ありえないが、該当するティアが見つからなかった場合は何も返さない（undefinedとなる）
      if (targetTier === undefined) {
        return
      }

      // 単価とクレジット数を掛け算して金額を計算
      return targetTier.unitAmountDecimal * creditCount
    },
    []
  )

  // オプション利用料(AIクレジット)の合計金額
  const aiCreditFeeAmount = useMemo(() => {
    // 単価がundefinedの場合は、正しい表示にならない恐れがあるのでundefinedを返すようにしておく
    if (
      aiCreditPriceTiersForPlansData === undefined ||
      isAiCreditPriceTiersForPlansFetching
    ) {
      return
    }

    // 選択されているプランや請求サイクルに応じた単価を取得
    const priceTiers = aiCreditPriceTiersForPlansData.plans
      .find((item) => item.plan === selectedPlan)
      ?.billingCycles.find(
        (item) => item.billingCycle === selectedBillingCycle
      )?.priceTiers

    if (priceTiers === undefined) {
      // undefinedになることはないが、undefinedになっていると正しい単価が取得できないのでundefinedを返すようにしておく
      return
    }

    // AIクレジットの金額を計算
    const calcAmount = calculateAiCreditAmount(
      inputtedContractAdditionalAiCreditCount,
      priceTiers
    )
    if (calcAmount === undefined) {
      // 該当するティアが見つからなかった場合は、正しい表示にならない恐れがあるのでundefinedを返すようにしておく
      return
    }

    return calcAmount
  }, [
    aiCreditPriceTiersForPlansData,
    calculateAiCreditAmount,
    inputtedContractAdditionalAiCreditCount,
    isAiCreditPriceTiersForPlansFetching,
    selectedBillingCycle,
    selectedPlan,
  ])

  // オプション利用料(AIクレジット)の金額表示
  const renderAiCreditFeeAmount = useCallback(() => {
    // 金額がundefinedの場合は、正しい表示にならない恐れがあるので何も表示しない
    if (aiCreditFeeAmount === undefined) {
      return ''
    }

    return t('label.cycleBilling.priceAmount', {
      price: aiCreditFeeAmount.toLocaleString(),
    })
  }, [t, aiCreditFeeAmount])

  // 外部ユーザーの無料枠の表示
  const renderExtUserFreeCount = useCallback(() => {
    if (!freeExtUserCountForSelectedPlan) {
      return ''
    }
    return t('label.cycleBilling.optionFreeQuantity', {
      quantity: freeExtUserCountForSelectedPlan.toLocaleString(),
    })
  }, [freeExtUserCountForSelectedPlan, t])

  // AIクレジットの無料枠の表示
  const renderAiCreditFreeCount = useCallback(() => {
    if (!getCurrentSubscriptionItemsCountResult) {
      return ''
    }

    return t('label.cycleBilling.optionFreeQuantity', {
      quantity: freeAiCreditCountForSelectedPlan.toLocaleString(),
    })
  }, [
    freeAiCreditCountForSelectedPlan,
    getCurrentSubscriptionItemsCountResult,
    t,
  ])

  /**
   * ご請求額の表示
   */

  // ご請求額
  const totalAmount = useMemo(() => {
    // 以下のそれぞれの合計金額が取得できていない場合は、正しい表示にならない恐れがあるのでundefinedを返すようにしておく
    //  * 基本サービス利用料
    //  * オプション利用料(外部ユーザー)
    //  * オプション利用料(ストレージ)
    //  * オプション利用料(AIクレジット)
    if (
      serviceFeeAmount === undefined ||
      extUserFeeAmount === undefined ||
      storageFeeAmount === undefined ||
      aiCreditFeeAmount === undefined
    ) {
      return
    }

    return (
      serviceFeeAmount + extUserFeeAmount + storageFeeAmount + aiCreditFeeAmount
    )
  }, [serviceFeeAmount, extUserFeeAmount, storageFeeAmount, aiCreditFeeAmount])

  // 請求額の表示
  const renderTotalAmount = useCallback(() => {
    // 合計金額が取得できていない場合は、正しい表示にならない恐れがあるので何も表示しない
    if (totalAmount === undefined) {
      return null
    }

    return (
      <div className="flex flex-col gap-y-1 ml-8">
        {/* 基本サービス利用料 */}
        <div className="flex flex-row items-center">
          <div className="w-48 crew-text-gray-5">
            {t('label.cycleBilling.serviceFeeLabel')}
          </div>
          {/* 購入数入力欄 */}
          <div>
            <CrewNumberBoxField
              control={control}
              id="contractUserCount"
              name="contractUserCount"
              step={1}
              showLabel={false}
              width={90}
              className="mx-1"
              onValueChanged={handleNumberOfContractUserCountValueChanged}
              min={
                getCurrentSubscriptionItemsCountResult?.requiredMinimumContractUserCount
              } // 取得した必要最小契約数を設定する
              showSpinButtons={true}
            />
          </div>
          <div className="text-crew-blue-3-light dark:text-crew-blue-2-dark">
            {t('label.cycleBilling.serviceFeeUnitLabel')}
          </div>
          <div className="ml-4 grow text-crew-blue-3-light dark:text-crew-blue-2-dark">
            {renderServiceFeeFormula()}
          </div>
          <div className="text-crew-blue-3-light dark:text-crew-blue-2-dark">
            {renderServiceFeeAmount()}
          </div>
        </div>

        {/* オプション */}
        <div className="flex flex-row items-center my-2">
          <div className="crew-text-gray-5">
            {t('label.cycleBilling.optionFeeLabel')}
          </div>
        </div>
        <div className="flex flex-col gap-y-1 ml-4">
          {/* オプション：外部ユーザー */}
          <div className="flex flex-row items-center">
            {/* 見出し + 無料枠の表示 */}
            <div className="flex justify-between w-44 crew-text-gray-5 ">
              <div>{t('label.cycleBilling.extUserOptionFeeLabel')}</div>
              <div className="mr-2">{renderExtUserFreeCount()}</div>
            </div>

            {/* 購入数入力欄 */}
            <div>
              <CrewNumberBoxField
                control={control}
                id="contractExternalUserAdditionalCount"
                name="contractExternalUserAdditionalCount"
                step={1}
                showLabel={false}
                width={90}
                className="mx-1"
                onValueChanged={
                  handleNumberOfContractExternalUserAdditionalCountValueChanged
                }
                // 一般ユーザーの購入数を増やすと、外部ユーザーの必要最小契約数が減るため、minは0を設定する
                // 例）
                //   表示時に「requiredMinimumContractExternalUserAdditionalCount」が1だったとしても、
                //   一般ユーザーの購入数を増やせば外部ユーザーオプションの購入は不要となるため、0を設定できる必要がある
                min={0}
                showSpinButtons={true}
              />
            </div>
            <div className="text-crew-blue-3-light dark:text-crew-blue-2-dark">
              {t('label.cycleBilling.extUserOptionFeeUnitLabel')}
            </div>
            <div className="ml-4 grow text-crew-blue-3-light dark:text-crew-blue-2-dark">
              {renderExtUserFeeFormula()}
            </div>
            <div className="text-crew-blue-3-light dark:text-crew-blue-2-dark">
              {renderExtUserFeeAmount()}
            </div>
          </div>

          {/* オプション：ストレージ */}
          <div className="flex flex-row items-center">
            <div className="w-44 crew-text-gray-5">
              {t('label.cycleBilling.storageOptionFeeLabel')}
            </div>
            {/* 購入数入力欄 */}
            <div>
              <CrewNumberBoxField
                control={control}
                id="contractS3StorageCapacity"
                name="contractS3StorageCapacity"
                step={10}
                showLabel={false}
                width={90}
                className="mx-1"
                onValueChanged={
                  handleNumberOfContractStorageCapacityValueChanged
                }
                min={MINIMUM_STORAGE_CAPACITY}
                showSpinButtons={true}
              />
            </div>
            <div className="text-crew-blue-3-light dark:text-crew-blue-2-dark">
              {t('label.cycleBilling.storageOptionFeeUnitLabel')}
            </div>
            {/* ここに表示する内容はないが、空の要素で表示位置を調整する */}
            <div className="grow"></div>
            <div className="text-crew-blue-3-light dark:text-crew-blue-2-dark">
              {renderStorageFeeAmount()}
            </div>
          </div>

          {/* オプション：AIクレジット */}
          <div className="flex flex-row items-center">
            {/* 見出し + 無料枠の表示 */}
            <div className="flex justify-between w-44 crew-text-gray-5 ">
              {t('label.cycleBilling.aiCreditOptionFeeLabel')}
              <div className="mr-2">{renderAiCreditFreeCount()}</div>
            </div>

            {/* 購入数入力欄 */}
            <div>
              <CrewNumberBoxField
                control={control}
                id="contractAdditionalAiCreditCount"
                name="contractAdditionalAiCreditCount"
                step={10}
                showLabel={false}
                width={90}
                className="mx-1"
                onValueChanged={
                  handleNumberOfContractAdditionalAiCreditCountValueChanged
                }
                min={0} // 追加分の入力のため最小値は0
                showSpinButtons={true}
              />
            </div>
            <div className="text-crew-blue-3-light dark:text-crew-blue-2-dark">
              {t('label.cycleBilling.aiCreditOptionFeeUnitLabel')}
            </div>
            {/* ここに表示する内容はないが、空の要素で表示位置を調整する */}
            <div className="grow"></div>
            <div className="text-crew-blue-3-light dark:text-crew-blue-2-dark">
              {renderAiCreditFeeAmount()}
            </div>
          </div>
        </div>
        {/* ご請求額 */}
        <div className="flex flex-row">
          {/* ここに表示する内容はないが、空の要素で表示位置を調整する */}
          <div className="grow"></div>
          <div className="w-20 crew-text-gray-5">
            {t('label.cycleBilling.totalAmountLabel')}
          </div>
          <div className="text-crew-blue-3-light dark:text-crew-blue-2-dark">
            {t('label.cycleBilling.priceAmount', {
              price: totalAmount.toLocaleString(),
            })}
          </div>
        </div>

        {/* 注意書き */}
        <div className="text-sm text-crew-red-3-light dark:text-crew-red-2-dark">
          {t('label.cycleBilling.purchaseNote', {
            freeStorageCapacity: MINIMUM_STORAGE_CAPACITY.toLocaleString(),
            freeAiCreditCount:
              freeAiCreditCountForSelectedPlan.toLocaleString(),
          })}
        </div>
      </div>
    )
  }, [
    totalAmount,
    t,
    control,
    handleNumberOfContractUserCountValueChanged,
    getCurrentSubscriptionItemsCountResult?.requiredMinimumContractUserCount,
    renderServiceFeeFormula,
    renderServiceFeeAmount,
    renderExtUserFreeCount,
    handleNumberOfContractExternalUserAdditionalCountValueChanged,
    renderExtUserFeeFormula,
    renderExtUserFeeAmount,
    handleNumberOfContractStorageCapacityValueChanged,
    renderStorageFeeAmount,
    renderAiCreditFreeCount,
    handleNumberOfContractAdditionalAiCreditCountValueChanged,
    renderAiCreditFeeAmount,
    freeAiCreditCountForSelectedPlan,
  ])

  return (
    <div className="flex justify-center">
      <div className="py-2.5 flex flex-col gap-2.5 w-full max-w-2xl">
        {/* 見出し（支払方法の変更/ご契約内容） */}
        <div className="py-2.5">
          <span className="text-md font-bold">
            {isPaid
              ? t('label.changePaymentMethod')
              : t('label.contractDetails')}
          </span>
        </div>

        {/* サブスクリプションステータスがトライアル中またはフリープランを利用中の場合は契約サイクルの選択を表示する */}
        {!isPaid && (
          <>
            {/* 契約プラン変更用のラジオボタン */}
            <div>
              <CrewFieldLabel text={t('label.contractPlan')} />
              <CrewRadioGroup
                layout="vertical"
                disabled={isFetching} // フェッチ中は選択不可
                items={planItems}
                valueExpr="value"
                displayExpr="name"
                value={selectedPlan}
                itemRender={PlanItem}
                onValueChanged={handlePlanRadioGroupValueChanged}
              />
            </div>

            {/* 請求サイクル変更用のラジオボタン */}
            <div className="flex flex-col gap-y-2">
              <div>
                <CrewFieldLabel text={t('label.contractCycle')} />

                <CrewRadioGroup
                  layout="vertical"
                  disabled={isFetching} // フェッチ中は選択不可
                  items={billingItems}
                  valueExpr="value"
                  displayExpr="name"
                  value={selectedBillingCycle}
                  itemRender={BillingCycleItem}
                  onValueChanged={handleBillingCycleRadioGroupValueChanged}
                />
              </div>

              {/* 契約プラン・契約サイクルの選択状態に応じて請求額を表示 */}
              {renderTotalAmount()}
            </div>
          </>
        )}

        {/* 支払い方法変更用のラジオボタン */}
        <div>
          <CrewFieldLabel text={t('label.paymentMethod')} />
          <CrewRadioGroup
            layout="vertical"
            dataSource={paymentMethodDataSource}
            valueExpr="id"
            displayExpr="name"
            value={selectedPaymentMethod}
            itemRender={renderPaymentRadioGroupItem}
            onValueChanged={handlePaymentMethodChanged}
            disabled={isCreateSetupIntentFetching || isFetching}
          />
        </div>

        {/* 登録フォーム */}
        {!isCreateSetupIntentFetching && (
          <div>
            {/* クレジットカード */}
            {selectedPaymentMethod === PaymentMethod.creditCard.value && (
              <Elements
                stripe={loadStripe(process.env.REACT_APP_STRIPE_KEY || '')}
                options={stripeElementOptions}
              >
                <ContractRegisterCreditCardForm
                  billingCycle={!isPaid ? selectedBillingCycle : undefined}
                  plan={!isPaid ? selectedPlan : undefined}
                  isPaid={isPaid}
                  showContactInformation={!isPaid}
                  // 「フリープランから有償プランへアップグレード」または「トライアル中」の時、Stripeの購入処理に各購入数が必要
                  purchaseUsersCount={
                    !isPaid ? inputtedContractUserCount : undefined
                  }
                  purchaseExternalUsersCount={
                    !isPaid
                      ? inputtedContractExternalUserAdditionalCount
                      : undefined
                  }
                  purchaseStorageCapacity={
                    !isPaid ? inputtedContractS3StorageCapacity : undefined
                  }
                  purchaseAdditionalAiCreditCount={
                    !isPaid
                      ? inputtedContractAdditionalAiCreditCount
                      : undefined
                  }
                />
              </Elements>
            )}
            {/* 銀行振込 */}
            {selectedPaymentMethod === PaymentMethod.bankTransfer.value && (
              <ContractRegisterBankTransferForm
                billingCycle={!isPaid ? selectedBillingCycle : undefined}
                plan={!isPaid ? selectedPlan : undefined}
                isPaid={isPaid}
                showContactInformation={!isPaid}
                // 「フリープランから有償プランへアップグレード」または「トライアル中」の時、Stripeの購入処理に各購入数が必要
                purchaseUsersCount={
                  !isPaid ? inputtedContractUserCount : undefined
                }
                purchaseExternalUsersCount={
                  !isPaid
                    ? inputtedContractExternalUserAdditionalCount
                    : undefined
                }
                purchaseStorageCapacity={
                  !isPaid ? inputtedContractS3StorageCapacity : undefined
                }
                purchaseAdditionalAiCreditCount={
                  !isPaid ? inputtedContractAdditionalAiCreditCount : undefined
                }
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
})
